import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { 
    MdMenu, 
    MdClose, 
    MdShoppingCart, 
    MdPerson,
    MdEmail,
    MdPhone,
    MdLocationOn,
    MdExpandMore,
    MdSearch
} from 'react-icons/md';
import lightLogo from '../../../public/light full-logo.png';
import darkLogo from '../../../public/dark logo.png';
import styles from '../../styles/DesignSystem.module.css';

export default function PublicLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [expandedDropdowns, setExpandedDropdowns] = useState<Record<string, boolean>>({});
    const [mobileExpandedItems, setMobileExpandedItems] = useState<Record<string, boolean>>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
    const isMobile = windowWidth < 768;

    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close mobile menu when route changes
    React.useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    // Close dropdowns when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // Check if click is inside navigation item container or dropdown content
            const navItem = target.closest('[data-nav-item]');
            const dropdown = target.closest('[data-dropdown]');
            
            // Close dropdown if click is outside both navigation items and dropdowns
            if (!navItem && !dropdown) {
                setExpandedDropdowns({});
            }
        };

        // Only add listener if any dropdown is open
        const hasOpenDropdown = Object.values(expandedDropdowns).some(Boolean);
        
        if (hasOpenDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
        
        return undefined;
    }, [expandedDropdowns]);

    const toggleDropdown = (key: string) => {
        setExpandedDropdowns(prev => {
            // If this dropdown is already open, close it
            if (prev[key]) {
                return { [key]: false };
            }
            // Otherwise, close all dropdowns and open this one
            return { [key]: true };
        });
    };

    const showDropdown = (key: string) => {
        setExpandedDropdowns(prev => ({
            ...prev,
            [key]: true
        }));
    };

    const hideDropdown = (key: string) => {
        setExpandedDropdowns(prev => ({
            ...prev,
            [key]: false
        }));
    };

    const toggleMobileMenuItem = (key: string) => {
        setMobileExpandedItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleLoginClick = () => {
        // Navigate to internal auth page
        navigate('/auth', { state: { from: location } });
    };

    const handleStaffLoginClick = () => {
        const isState = import.meta.env.NODE_ENV;
        var location = '';
        switch (isState) { 
            case "production": 
                location = 'https://store.gram.com.au/login';
            break; 

            case "development": 
                location = 'http://192.168.0.233:34550/customer/login?returnTo=%2Fcustomer%2Fproduct-selection'
            break;

        }
        // Link to external admin dashboard
        //window.location.href = location
        // no implementation as of yet

        alert("Staff Portal - Coming Soon")
    };

    const handleRegisterClick = () => {
        // Link to external customer portal registration
        window.location.href = 'https://portal.gram.com.au/register';
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const navigationItems = [
        {
            label: 'HOME',
            path: '/',
            subItems: []
        },
        {
            label: 'OUTLETS',
            path: '/outlets',
            subItems: []
        },
        {
            label: 'PRODUCTS',
            path: '/products',
            subItems: [
                // SPF STEEL PRIVACY FENCING
                { label: 'SPF® Steel Privacy Fencing', path: '/products/spf-steel' },
                { label: 'Gramline Infill Sheet', path: '/products/gramline-infill-sheet' },
                { label: 'ColourLine Infill Sheet', path: '/products/colourline-infill-sheet' },
                { label: 'Slimline Framing', path: '/products/slimline-framing' },
                { label: 'SmartPost Framing Post', path: '/products/smartpost-framing-post' },
                { label: 'Steel Plinth Retaining Walls', path: '/products/steel-plinth-retaining-walls' },
                { label: 'Gates by Gramline', path: '/products/gates-by-gramline' },
                { label: 'Accessories', path: '/products/accessories' },
                // STEEL PLINTH
                { label: 'Steel Plinth™', path: '/products/steel-plinth' },
                // GRAMLAT 3D LATTICE
                { label: 'GramLat® 3D Lattice™', path: '/products/gramlat-3d' },
                { label: 'Punched Lattice', path: '/products/gramlat-3d/punched-lattice' },
                // SYSTEMS
                { label: 'Gramslat Extension Kit', path: '/products/gramslat-extension-kit' },
                { label: 'SmartSlat', path: '/products/smartslat' },
                { label: 'Floodway Louvres', path: '/products/floodway-louvres' },
                // FENCING
                { label: 'Security Fencing & Punched Tubing', path: '/products/security-fencing' },
                { label: 'Childgard™ Pool Fencing', path: '/products/pool-fencing' },
                { label: 'Childgard™ Garden Fencing', path: '/products/garden-fencing' },
                // HARDWARE
                { label: 'Hardware', path: '/products/hardware' },
                // RESOURCES
                { label: 'Colour Range', path: '/specification/colours' },
                { label: 'Brochures', path: '/specification/brochures' },
                { label: 'Installation', path: '/specification/installation' },
                { label: 'Warranty', path: '/specification/warranty' },
                { label: 'Videos', path: '/specification/videos' }
            ],
            groups: [
                {
                    title: 'SPF STEEL PRIVACY FENCING',
                    items: [
                        { label: 'SPF® Steel Privacy Fencing', path: '/products/spf-steel' },
                        { label: 'Gramline Infill Sheet', path: '/products/gramline-infill-sheet' },
                        { label: 'ColourLine Infill Sheet', path: '/products/colourline-infill-sheet' },
                        { label: 'Slimline Framing', path: '/products/slimline-framing' },
                        { label: 'SmartPost Framing Post', path: '/products/smartpost-framing-post' },
                        { label: 'Steel Plinth Retaining Walls', path: '/products/steel-plinth-retaining-walls' },
                        { label: 'Gates by Gramline', path: '/products/gates-by-gramline' },
                        { label: 'Accessories', path: '/products/accessories' }
                    ]
                },
                {
                    title: 'FENCING',
                    items: [
                        { label: 'Security Fencing & Punched Tubing', path: '/products/security-fencing' },
                        { label: 'Childgard™ Pool Fencing', path: '/products/pool-fencing' },
                        { label: 'Standard Style', path: '/products/pool-fencing/standard-style' },
                        { label: 'Decorative Style', path: '/products/pool-fencing/decorative-style' },
                        { label: 'Childgard™ Garden Fencing', path: '/products/garden-fencing' },
                        { label: 'Standard Styles', path: '/products/garden-fencing/standard-styles' },
                        { label: 'Decorative Styles', path: '/products/garden-fencing/decorative-styles' },
                        { label: 'Steel Picket', path: '/products/garden-fencing/steel-picket' }
                    ]
                },
                {
                    title: 'RESOURCES',
                    items: [
                        { label: 'Colour Range', path: '/specification/colours' },
                        { label: 'Brochures', path: '/specification/brochures' },
                        { label: 'Installation', path: '/specification/installation' },
                        { label: 'Warranty', path: '/specification/warranty' },
                        { label: 'Videos', path: '/specification/videos' }
                    ]
                },
                {
                    title: 'HARDWARE',
                    items: [
                        { label: 'Hardware', path: '/products/hardware' },
                        { label: 'Stainless Steel Fittings', path: '/products/hardware/stainless-steel-fittings' },
                        { label: 'Caps - Plastic & Metal', path: '/products/hardware/caps' },
                        { label: 'Cement', path: '/products/hardware/cement' },
                        { label: 'Decorative Fittings', path: '/products/hardware/decorative-fittings' },
                        { label: 'Fasteners', path: '/products/hardware/fasteners' },
                        { label: 'Hinges', path: '/products/hardware/hinges' },
                        { label: 'Locks', path: '/products/hardware/locks' },
                        { label: 'Paints', path: '/products/hardware/paints' },
                        { label: 'Post Accessories', path: '/products/hardware/post-accessories' },
                        { label: 'Tools', path: '/products/hardware/tools' },
                        { label: 'Sliding Gate Fittings', path: '/products/hardware/sliding-gate-fittings' },
                        { label: 'SPF Accessories', path: '/products/hardware/spf-accessories' }
                    ]
                },
                {
                    title: 'STEEL PLINTH',
                    items: [
                        { label: 'Steel Plinth™', path: '/products/steel-plinth' }
                    ]
                },
                {
                    title: 'GRAMLAT 3D LATTICE',
                    items: [
                        { label: 'GramLat® 3D Lattice™', path: '/products/gramlat-3d' },
                        { label: 'Punched Lattice', path: '/products/gramlat-3d/punched-lattice' }
                    ]
                },
                {
                    title: 'SYSTEMS',
                    items: [
                        { label: 'Gramslat Extension Kit', path: '/products/gramslat-extension-kit' },
                        { label: 'SmartSlat', path: '/products/smartslat' },
                        { label: 'Floodway Louvres', path: '/products/floodway-louvres' }
                    ]
                }
            ]
        },

        {
            label: 'TRADE',
            path: '/purchasing',
            subItems: [
                { label: 'Get a Quote', path: '/purchasing/quote' },
                { label: 'Trade Login', path: 'http://www.gramapi.com.au:81/GramEngineering/', isExternal: true },
                { label: 'Register', path: '/customer/register', isExternal: true }
            ]
        },
        {
            label: 'ABOUT US',
            path: '/about',
            subItems: [
                { label: 'History', path: '/about/history' }
            ]
        },
        {
            label: 'CONTACT',
            path: '/contact',
            subItems: []
        },
        {
            label: 'ARTICLES',
            path: '/articles',
            subItems: []
        }
    ];

    const isActive = (path: string) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-secondary)'
        }}>
            {/* Add animation styles */}
            <style>
                {`
                    @keyframes slideDown {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    @keyframes slideIn {
                        from {
                            transform: translateX(-100%);
                        }
                        to {
                            transform: translateX(0);
                        }
                    }
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                `}
            </style>
            {/* Header */}
            <header style={{
                background: 'var(--bg-primary)',
                borderBottom: '1px solid var(--border-light)',
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                boxShadow: 'var(--shadow-sm)',
                overflow: 'visible'
            }}>
                {/* Top Bar */}
                {windowWidth >= 480 && (
                    <div style={{
                        background: 'var(--primary-600)',
                        color: 'white',
                        padding: 'var(--space-3) var(--space-0)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <div style={{
                            maxWidth: '1400px',
                            margin: '0 auto',
                            padding: '0 var(--space-6)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: windowWidth < 640 ? 'wrap' : 'nowrap',
                            gap: windowWidth < 640 ? 'var(--space-2)' : '0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: windowWidth < 640 ? 'var(--space-3)' : 'var(--space-6)', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <MdPhone style={{ fontSize: '16px' }} />
                                    <span>(02) 8788 8200</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <MdEmail style={{ fontSize: '16px' }} />
                                    <span>info@gram.com.au</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation with Logo, Search, and Login */}
                <nav style={{
                    background: 'var(--bg-primary)',
                    overflow: 'visible',
                    borderBottom: '1px solid var(--border-light)',
                    position: 'relative'
                }}>
                    <div style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 var(--space-6)',
                        overflow: 'visible'
                    }}>
                        {/* Top Row - Logo, Search, Login */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? 'auto 1fr auto' : 'auto 1fr auto',
                            alignItems: 'center',
                            gap: isMobile ? 'var(--space-3)' : 'var(--space-6)',
                            padding: isMobile ? 'var(--space-3) 0' : 'var(--space-4) 0'
                        }}>
                            {/* Hamburger Menu Button (Mobile) */}
                            {isMobile && (
                                <button
                                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--text-primary)',
                                        cursor: 'pointer',
                                        padding: 'var(--space-2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        minWidth: '44px',
                                        minHeight: '44px'
                                    }}
                                    aria-label="Toggle menu"
                                >
                                    {isMobileMenuOpen ? <MdClose /> : <MdMenu />}
                                </button>
                            )}

                            {/* Logo */}
                            <Link to="/" style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                flexShrink: 0,
                                justifySelf: isMobile ? 'start' : 'auto'
                            }}>
                                <img 
                                    src={lightLogo} 
                                    alt="Gramline" 
                                    style={{ height: isMobile ? '40px' : '50px' }}
                                />
                            </Link>

                            {/* Search Bar - Hidden on very small screens, shown on mobile+ */}
                            {windowWidth >= 480 && (
                                <form onSubmit={handleSearch} style={{ 
                                    width: '100%',
                                    maxWidth: isMobile ? '100%' : '500px',
                                    justifySelf: 'center',
                                    display: isMobile && windowWidth < 640 ? 'none' : 'block'
                                }}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            placeholder="Search products..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: 'var(--space-3) var(--space-4) var(--space-3) var(--space-12)',
                                                border: '1px solid var(--border-medium)',
                                                borderRadius: 'var(--radius-md)',
                                                fontSize: 'var(--font-size-sm)',
                                                background: 'var(--bg-primary)',
                                                color: 'var(--text-primary)'
                                            }}
                                        />
                                        <MdSearch style={{
                                            position: 'absolute',
                                            left: 'var(--space-4)',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: 'var(--text-tertiary)',
                                            fontSize: '20px'
                                        }} />
                                    </div>
                                </form>
                            )}

                            {/* Login/Register Buttons */}
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: isMobile ? 'var(--space-1)' : 'var(--space-2)',
                                flexShrink: 0
                            }}>
                                {!isMobile && (
                                    <button
                                        onClick={handleStaffLoginClick}
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid var(--border-medium)',
                                            color: 'var(--text-primary)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            borderRadius: 'var(--radius-sm)',
                                            fontSize: 'var(--font-size-xs)',
                                            cursor: 'pointer',
                                            transition: 'var(--transition-fast)',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onMouseEnter={(e) => (e.target as HTMLElement).style.background = 'var(--bg-secondary)'}
                                        onMouseLeave={(e) => (e.target as HTMLElement).style.background = 'transparent'}
                                    >
                                        Staff
                                    </button>
                                )}
                                <button
                                    onClick={handleLoginClick}
                                    style={{
                                        background: 'var(--primary-600)',
                                        border: '1px solid var(--primary-600)',
                                        color: 'white',
                                        padding: isMobile ? 'var(--space-2)' : 'var(--space-2) var(--space-3)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: isMobile ? 'var(--font-size-xs)' : 'var(--font-size-xs)',
                                        cursor: 'pointer',
                                        transition: 'var(--transition-fast)',
                                        whiteSpace: 'nowrap',
                                        minWidth: '44px',
                                        minHeight: '44px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => (e.target as HTMLElement).style.background = 'var(--primary-700)'}
                                    onMouseLeave={(e) => (e.target as HTMLElement).style.background = 'var(--primary-600)'}
                                >
                                    {isMobile ? <MdPerson style={{ fontSize: '20px' }} /> : 'Register / Login'}
                                </button>
                            </div>
                        </div>

                        {/* Navigation Menu - Hidden on Mobile */}
                        {!isMobile && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-0)',
                                position: 'relative',
                                overflow: 'visible',
                                borderTop: '1px solid var(--border-light)',
                                paddingTop: 'var(--space-2)',
                                paddingBottom: 'var(--space-2)'
                            }}>
                            {navigationItems.map((item) => (
                                <div 
                                    key={item.path} 
                                    data-nav-item
                                    style={{ 
                                        position: 'static',
                                        display: 'inline-block'
                                    }}
                                >
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (item.subItems && item.subItems.length > 0) {
                                                // For items with dropdowns, toggle dropdown
                                                toggleDropdown(item.path);
                                            } else {
                                                navigate(item.path);
                                            }
                                        }}
                                        style={{
                                            padding: 'var(--space-4) var(--space-5)',
                                            background: isActive(item.path) ? 'var(--primary-600)' : 'transparent',
                                            color: isActive(item.path) ? 'white' : 'var(--text-primary)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 'var(--font-weight-medium)',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-1)',
                                            whiteSpace: 'nowrap',
                                            borderRadius: 'var(--radius-sm)'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive(item.path)) {
                                                (e.target as HTMLElement).style.background = 'var(--bg-secondary)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive(item.path)) {
                                                (e.target as HTMLElement).style.background = 'transparent';
                                            }
                                        }}
                                    >
                                        {item.label}
                                        {item.subItems && item.subItems.length > 0 && (
                                            <MdExpandMore 
                                                style={{ 
                                                    fontSize: '16px',
                                                    transform: expandedDropdowns[item.path] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s ease'
                                                }} 
                                            />
                                        )}
                                    </button>

                                    {/* Dropdown Menu */}
                                    {item.subItems && item.subItems.length > 0 && expandedDropdowns[item.path] && (
                                        <div 
                                            data-dropdown
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '100vw',
                                                paddingTop: '0',
                                                zIndex: 1001
                                            }}
                                        >
                                            <div
                                                style={{
                                                    background: 'var(--bg-primary)',
                                                    borderTop: '3px solid var(--primary-600)',
                                                    borderBottom: '1px solid var(--border-light)',
                                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                                    animation: 'slideDown 0.2s ease-out'
                                                }}
                                            >
                                                <div style={{
                                                    maxWidth: '1400px',
                                                    margin: '0 auto',
                                                    padding: windowWidth >= 1024 ? 'var(--space-10) var(--space-6) var(--space-8) var(--space-6)' : 'var(--space-8) var(--space-4) var(--space-6) var(--space-4)',
                                                    maxHeight: 'calc(100vh - 200px)',
                                                    overflowY: 'auto',
                                                    overflowX: 'hidden'
                                                }}>
                                                    {item.groups && item.groups.length > 0 ? (
                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: windowWidth < 640
                                                                ? '1fr'
                                                                : (windowWidth < 1024
                                                                    ? 'repeat(2, minmax(0, 1fr))'
                                                                    : 'repeat(auto-fit, minmax(240px, 1fr))'),
                                                            gap: windowWidth < 640 ? 'var(--space-6)' : 'var(--space-8)',
                                                            columnGap: windowWidth >= 1024 ? 'var(--space-12)' : 'var(--space-8)'
                                                        }}>
                                                            {item.groups.map((group) => (
                                                                <div key={group.title}>
                                                                    <div style={{
                                                                        fontSize: windowWidth < 640 ? 'var(--font-size-sm)' : 'var(--font-size-xs)',
                                                                        fontWeight: 'var(--font-weight-bold)',
                                                                        letterSpacing: '0.06em',
                                                                        textTransform: 'uppercase',
                                                                        color: 'var(--primary-600)',
                                                                        marginBottom: 'var(--space-4)',
                                                                        paddingBottom: 'var(--space-2)',
                                                                        borderBottom: '2px solid var(--primary-100)'
                                                                    }}>
                                                                        {group.title}
                                                                    </div>
                                                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                                        {group.items.map((sub) => (
                                                                            <li key={sub.path} style={{ marginBottom: 'var(--space-1)' }}>
                                                                                <Link
                                                                                    to={sub.path}
                                                                                    style={{
                                                                                        display: 'block',
                                                                                        color: 'var(--text-secondary)',
                                                                                        textDecoration: 'none',
                                                                                        fontSize: windowWidth < 640 ? 'var(--font-size-md)' : 'var(--font-size-sm)',
                                                                                        padding: windowWidth < 640 ? 'var(--space-3) var(--space-2)' : 'var(--space-2) var(--space-3)',
                                                                                        borderRadius: 'var(--radius-md)',
                                                                                        transition: 'all 0.15s ease',
                                                                                        fontWeight: 'var(--font-weight-medium)'
                                                                                    }}
                                                                                    onMouseEnter={(e) => { 
                                                                                        (e.target as HTMLElement).style.color = 'var(--primary-700)'; 
                                                                                        (e.target as HTMLElement).style.background = 'var(--primary-50)';
                                                                                        (e.target as HTMLElement).style.paddingLeft = 'var(--space-4)';
                                                                                    }}
                                                                                    onMouseLeave={(e) => { 
                                                                                        (e.target as HTMLElement).style.color = 'var(--text-secondary)'; 
                                                                                        (e.target as HTMLElement).style.background = 'transparent';
                                                                                        (e.target as HTMLElement).style.paddingLeft = 'var(--space-3)';
                                                                                    }}
                                                                                    onClick={() => hideDropdown(item.path)}
                                                                                >
                                                                                    {sub.label}
                                                                                </Link>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <ul style={{ 
                                                            listStyle: 'none', 
                                                            padding: 0, 
                                                            margin: 0,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 'var(--space-1)'
                                                        }}>
                                                            {item.subItems.map((subItem) => (
                                                                <li key={subItem.path}>
                                                                    <Link 
                                                                        to={subItem.path} 
                                                                        style={{ 
                                                                            display: 'block', 
                                                                            color: 'var(--text-secondary)', 
                                                                            textDecoration: 'none', 
                                                                            fontSize: 'var(--font-size-base)', 
                                                                            padding: 'var(--space-3) var(--space-4)', 
                                                                            borderRadius: 'var(--radius-md)',
                                                                            transition: 'all 0.15s ease',
                                                                            fontWeight: 'var(--font-weight-medium)'
                                                                        }} 
                                                                        onMouseEnter={(e) => { 
                                                                            (e.target as HTMLElement).style.color = 'var(--primary-700)'; 
                                                                            (e.target as HTMLElement).style.background = 'var(--primary-50)';
                                                                            (e.target as HTMLElement).style.paddingLeft = 'var(--space-5)';
                                                                        }} 
                                                                        onMouseLeave={(e) => { 
                                                                            (e.target as HTMLElement).style.color = 'var(--text-secondary)'; 
                                                                            (e.target as HTMLElement).style.background = 'transparent';
                                                                            (e.target as HTMLElement).style.paddingLeft = 'var(--space-4)';
                                                                        }} 
                                                                        onClick={() => hideDropdown(item.path)}
                                                                    >
                                                                        {subItem.label}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        )}
                    </div>
                </nav>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && isMobile && (
                    <>
                        {/* Backdrop */}
                        <div
                            onClick={() => setIsMobileMenuOpen(false)}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 1299,
                                animation: 'fadeIn 0.2s ease-out'
                            }}
                        />
                        {/* Mobile Menu Drawer */}
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: 'min(320px, 85vw)',
                            background: 'var(--bg-primary)',
                            boxShadow: 'var(--shadow-xl)',
                            zIndex: 1300,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            animation: 'slideIn 0.3s ease-out',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Mobile Menu Header */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: 'var(--space-4)',
                                borderBottom: '1px solid var(--border-light)',
                                background: 'var(--bg-primary)',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                <img 
                                    src={lightLogo} 
                                    alt="Gramline" 
                                    style={{ height: '40px' }}
                                />
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--text-primary)',
                                        cursor: 'pointer',
                                        padding: 'var(--space-2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        minWidth: '44px',
                                        minHeight: '44px'
                                    }}
                                    aria-label="Close menu"
                                >
                                    <MdClose />
                                </button>
                            </div>

                            {/* Mobile Menu Items */}
                            <div style={{
                                flex: 1,
                                padding: 'var(--space-2) 0',
                                overflowY: 'auto'
                            }}>
                                {navigationItems.map((item) => (
                                    <div key={item.path}>
                                        {item.subItems && item.subItems.length > 0 ? (
                                            <>
                                                <button
                                                    onClick={() => toggleMobileMenuItem(item.path)}
                                                    style={{
                                                        width: '100%',
                                                        padding: 'var(--space-4) var(--space-4)',
                                                        background: isActive(item.path) ? 'var(--primary-600)' : 'transparent',
                                                        color: isActive(item.path) ? 'white' : 'var(--text-primary)',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        fontSize: 'var(--font-size-base)',
                                                        fontWeight: 'var(--font-weight-medium)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        textAlign: 'left',
                                                        minHeight: '44px',
                                                        transition: 'background 0.2s ease'
                                                    }}
                                                >
                                                    <span>{item.label}</span>
                                                    <MdExpandMore 
                                                        style={{ 
                                                            fontSize: '20px',
                                                            transform: mobileExpandedItems[item.path] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.2s ease'
                                                        }} 
                                                    />
                                                </button>
                                                {mobileExpandedItems[item.path] && (
                                                    <div style={{
                                                        background: 'var(--bg-secondary)',
                                                        padding: 'var(--space-2) 0'
                                                    }}>
                                                        {item.groups && item.groups.length > 0 ? (
                                                            item.groups.map((group) => (
                                                                <div key={group.title} style={{ marginBottom: 'var(--space-4)' }}>
                                                                    <div style={{
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        fontWeight: 'var(--font-weight-bold)',
                                                                        letterSpacing: '0.06em',
                                                                        textTransform: 'uppercase',
                                                                        color: 'var(--primary-600)',
                                                                        padding: 'var(--space-3) var(--space-4) var(--space-2) var(--space-4)',
                                                                        borderBottom: '1px solid var(--border-light)'
                                                                    }}>
                                                                        {group.title}
                                                                    </div>
                                                                    {group.items.map((sub) => (
                                                                        <Link
                                                                            key={sub.path}
                                                                            to={sub.path}
                                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                                            style={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                color: 'var(--text-secondary)',
                                                                                textDecoration: 'none',
                                                                                fontSize: 'var(--font-size-sm)',
                                                                                padding: 'var(--space-3) var(--space-4) var(--space-3) var(--space-6)',
                                                                                transition: 'all 0.15s ease',
                                                                                fontWeight: 'var(--font-weight-medium)',
                                                                                minHeight: '44px'
                                                                            }}
                                                                        >
                                                                            {sub.label}
                                                                        </Link>
                                                                    ))}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            item.subItems.map((subItem) => (
                                                                <Link
                                                                    key={subItem.path}
                                                                    to={subItem.path}
                                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        color: 'var(--text-secondary)',
                                                                        textDecoration: 'none',
                                                                        fontSize: 'var(--font-size-sm)',
                                                                        padding: 'var(--space-3) var(--space-4)',
                                                                        transition: 'all 0.15s ease',
                                                                        fontWeight: 'var(--font-weight-medium)',
                                                                        minHeight: '44px'
                                                                    }}
                                                                >
                                                                    {subItem.label}
                                                                </Link>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <Link
                                                to={item.path}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: 'var(--space-4) var(--space-4)',
                                                    background: isActive(item.path) ? 'var(--primary-600)' : 'transparent',
                                                    color: isActive(item.path) ? 'white' : 'var(--text-primary)',
                                                    textDecoration: 'none',
                                                    fontSize: 'var(--font-size-base)',
                                                    fontWeight: 'var(--font-weight-medium)',
                                                    transition: 'background 0.2s ease',
                                                    minHeight: '44px'
                                                }}
                                            >
                                                {item.label}
                                            </Link>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </header>

            {/* Main Content */}
            <main style={{ flex: 1 }}>
                <Outlet />
            </main>

            {/* Footer */}
            <footer style={{
                background: 'var(--bg-primary)',
                borderTop: '1px solid var(--border-light)',
                padding: isMobile ? 'var(--space-8) var(--space-0)' : 'var(--space-16) var(--space-0)',
                marginTop: 'auto'
            }}>
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: isMobile ? '0 var(--space-4)' : '0 var(--space-6)',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: isMobile ? 'var(--space-6)' : 'var(--space-12)'
                }}>
                    {/* Company Info */}
                    <div>
                        <img 
                            src={lightLogo} 
                            alt="Gramline" 
                            style={{ height: '40px', marginBottom: 'var(--space-4)' }}
                        />
                        <p style={{
                            color: 'var(--text-secondary)',
                            fontSize: 'var(--font-size-sm)',
                            lineHeight: '1.6',
                            marginBottom: 'var(--space-4)'
                        }}>
                            Australia's leading manufacturer of steel privacy fencing and security products.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                            <MdPhone style={{ color: 'var(--text-tertiary)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
                                (02) 8788 8200
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <MdEmail style={{ color: 'var(--text-tertiary)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
                                info@gram.com.au
                            </span>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: 'var(--text-primary)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            Quick Links
                        </h3>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {[
                                { label: 'Get a Quote', path: '/purchasing/quote' },
                                { label: 'Trade Login', path: 'http://www.gramapi.com.au:81/GramEngineering/', isExternal: true },
                                { label: 'Product Catalog', path: '/products' },
                                { label: 'Technical Specifications', path: '/specification/technical' },
                                { label: 'Contact Us', path: '/contact' }
                            ].map((link) => (
                                <li key={link.path} style={{ marginBottom: 'var(--space-2)' }}>
                                    <Link
                                        to={link.path}
                                        style={{
                                            color: 'var(--text-secondary)',
                                            textDecoration: 'none',
                                            fontSize: 'var(--font-size-sm)',
                                            transition: 'var(--transition-fast)'
                                        }}
                                        onMouseEnter={(e) => (e.target as HTMLElement).style.color = 'var(--primary-600)'}
                                        onMouseLeave={(e) => (e.target as HTMLElement).style.color = 'var(--text-secondary)'}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Product Categories */}
                    <div>
                        <h3 style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: 'var(--text-primary)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            Products
                        </h3>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {[
                                { label: 'SPF® Steel Privacy Fencing', path: '/products/spf-steel' },
                                { label: 'Steel Plinth™', path: '/products/steel-plinth' },
                                { label: 'GramLat® 3D Lattice™', path: '/products/gramlat-3d' },
                                { label: 'View All Products', path: '/products' }
                            ].map((category) => (
                                <li key={category.path} style={{ marginBottom: 'var(--space-2)' }}>
                                    <Link
                                        to={category.path}
                                        style={{
                                            color: 'var(--text-secondary)',
                                            textDecoration: 'none',
                                            fontSize: 'var(--font-size-sm)',
                                            transition: 'var(--transition-fast)'
                                        }}
                                        onMouseEnter={(e) => (e.target as HTMLElement).style.color = 'var(--primary-600)'}
                                        onMouseLeave={(e) => (e.target as HTMLElement).style.color = 'var(--text-secondary)'}
                                    >
                                        {category.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Support */}
                    <div>
                        <h3 style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: 'var(--text-primary)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            Support
                        </h3>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {[
                                { label: 'Technical Specifications', path: '/specification/technical' },
                                { label: 'Colour Range', path: '/specification/colours' },
                                { label: 'Contact Us', path: '/contact' },
                                { label: 'About Us', path: '/about' }
                            ].map((link) => (
                                <li key={link.path} style={{ marginBottom: 'var(--space-2)' }}>
                                    <Link
                                        to={link.path}
                                        style={{
                                            color: 'var(--text-secondary)',
                                            textDecoration: 'none',
                                            fontSize: 'var(--font-size-sm)',
                                            transition: 'var(--transition-fast)'
                                        }}
                                        onMouseEnter={(e) => (e.target as HTMLElement).style.color = 'var(--primary-600)'}
                                        onMouseLeave={(e) => (e.target as HTMLElement).style.color = 'var(--text-secondary)'}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom Footer */}
                <div style={{
                    borderTop: '1px solid var(--border-light)',
                    marginTop: isMobile ? 'var(--space-6)' : 'var(--space-12)',
                    paddingTop: isMobile ? 'var(--space-4)' : 'var(--space-6)',
                    textAlign: 'center',
                    maxWidth: '1400px',
                    margin: isMobile ? 'var(--space-6) auto 0 auto' : 'var(--space-12) auto 0 auto',
                    padding: isMobile ? 'var(--space-4) var(--space-4) 0 var(--space-4)' : 'var(--space-6) var(--space-6) 0 var(--space-6)'
                }}>
                    <p style={{
                        color: 'var(--text-tertiary)',
                        fontSize: 'var(--font-size-sm)',
                        margin: 0
                    }}>
                        © {new Date().getFullYear()} Gramline Pty Ltd. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
} 